import { useQuery } from '@apollo/client'
import type { ComboboxItem } from '@/components'

import { ALL_ISSUE_PROCESSES_QUERY } from '../queries'
import type { AllIssueProcessesQuery } from '../types.graphl'

export interface IssueProcessFieldApi {
  /** At least one process with at least one choice exists. */
  hasProcesses: boolean

  /**
   * Flat choice options for a single combobox, ordered by process then choice
   * order. When more than one process exists each label is prefixed with its
   * process name (`Process – Choice`) so the flat list still reads as grouped.
   */
  options: ComboboxItem<string>[]

  /** Label of the currently selected choice, for the read-only view. */
  selectedLabel: string

  /** Grid cells rendered (1 when processes exist, else 0). */
  cellCount: () => number
}

/**
 * Shared logic for the issue "process" picker, used on both issue creation
 * and editing.
 *   - no process defined          → nothing to show (hasProcesses = false)
 *   - one process defined         → choices listed by their own name
 *   - multiple processes defined  → choices from all processes in one list,
 *                                   each labelled `Process – Choice`
 *
 * `value` is the selected `processChoice` id.
 */
export const useIssueProcessField = (
  value: string | null
): IssueProcessFieldApi => {
  const { data } = useQuery<AllIssueProcessesQuery>(ALL_ISSUE_PROCESSES_QUERY)

  const processes = (
    data?.allIssueProcesses?.edges.map(e => e.node) ?? []
  ).filter(process => process.choices.edges.length > 0)
  const multiple = processes.length > 1

  const options: ComboboxItem<string>[] = processes
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name))
    .flatMap(process =>
      process.choices.edges
        .slice()
        .sort((a, b) => a.node.order - b.node.order)
        .map(({ node }) => ({
          value: node.id,
          label: multiple ? `${process.name} – ${node.name}` : node.name,
        }))
    )

  const hasProcesses = options.length > 0
  const selectedLabel =
    (options.find(option => option.value === value)?.label as string) ?? '-'

  return {
    hasProcesses,
    options,
    selectedLabel,
    cellCount: () => (hasProcesses ? 1 : 0),
  }
}
